<!-- @format -->
<template>
    <div class="social-media-inbox-private-messages-widget" v-cloak>
        <div class="widget-header">
            <h3 class="h-d-flex h-align-items-center">
                <p22-private-messages-svg class="private-message-icon" />
                Private Message
            </h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>

        <div class="content-wrapper h-d-flex h-flex-column">
            <template v-if="isLoading">
                <div class="loading-screen-container">
                    <loading-screen-svg />
                </div>
            </template>
            <template v-else>
                <div
                    v-if="blockReply || options.isClosed"
                    class="card-conversation-closed text-center"
                >
                    <span>
                        <template v-if="options.isClosed">
                            <strong class="text-capitalize">{{ options.closedBy }}</strong>
                            closed this conversation on
                            <strong>{{ $moment(options.closedDate) | momentDateMMDDYYYY }}</strong>
                            at
                            <strong>{{ $moment(options.closedDate) | momentTime }}</strong>
                        </template>
                        <template v-else-if="blockReply">
                            This message is outside of allowed response period.
                        </template>
                    </span>
                </div>

                <social-inbox-message-history
                    :messages="messages"
                    :sender-id="senderId"
                    :users-profile-image="usersProfileImages"
                />

                <social-inbox-reply-message
                    v-if="!options.isClosed && !blockReply"
                    ref="replyMessageInput"
                    v-model="replyMessage"
                    :client-id="options.clientId"
                    :show-profile-img="false"
                    :social-network="options.socialNetwork"
                />
            </template>
        </div>

        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" :on-click="closeWidget">Close</app-button>
            </div>
            <div class="action-container">
                <app-button
                    :class="{ 'white-btn': options.isClosed }"
                    :disabled="disableBtn || (!options.isClosed && blockReply)"
                    :loading="loadingBtn"
                    :on-click="handleClick"
                >
                    <template v-if="options.isClosed">
                        <t26-builder-reopen />
                        <span>Re-open Conversation</span>
                    </template>
                    <template v-else>Send</template>
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { SocialMediaPostConversationsService } from '@/scripts/services/social-media/social-media-post-conversations-service';

const AppButton = () => import('@/components/common/app-button');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const P22PrivateMessagesSvg = () => import('@/components/svg/grid/p22-private-messages-svg');
const SocialInboxMessageHistory = () =>
    import('@/components/social-media/inbox/social-inbox-message-history');
const SocialInboxReplyMessage = () =>
    import('@/components/social-media/inbox/social-inbox-reply-message');
const T26tBuilderReopen = () => import('@/components/svg/grid/t26-builder-reopen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

export default {
    name: 'social-media-inbox-private-messages-widget',

    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'p22-private-messages-svg': P22PrivateMessagesSvg,
        'social-inbox-message-history': SocialInboxMessageHistory,
        'social-inbox-reply-message': SocialInboxReplyMessage,
        't26-builder-reopen': T26tBuilderReopen,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },

        options: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            disableBtn: false,
            hasReachedTimeLimit: false,
            isLoading: true,
            loadingBtn: false,
            messages: [],
            messagesRefreshInterval: null,
            mouseDetectionTimeOut: null,
            recipientId: null,
            replyMessage: {
                image: null,
                message: '',
            },
            senderId: null,
            usersProfileImages: [],
        };
    },

    computed: {
        blockReply() {
            return this.hasReachedTimeLimit && this.options.socialNetwork === 'facebook';
        },
    },

    async created() {
        await this.init();
    },

    beforeMount() {
        document.addEventListener('mousemove', this.initMouseMoveDetection);
    },

    beforeDestroy() {
        document.removeEventListener('mousemove', this.initMouseMoveDetection);
        this.destroyMessagesRefreshInterval();

        if (this.mouseDetectionTimeOut) {
            clearTimeout(this.mouseDetectionTimeOut);
        }
    },

    methods: {
        async init() {
            this.isLoading = true;
            await this.getMessages();
            this.isLoading = false;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        destroyMessagesRefreshInterval() {
            if (this.messagesRefreshInterval) {
                clearInterval(this.messagesRefreshInterval);
                this.messagesRefreshInterval = null;
            }
        },

        getLastSenderMessageDay() {
            const items = this.messages.reduce((result, message) => {
                if (message.SenderId === this.senderId) {
                    result.push(message);
                }

                return result;
            }, []);

            if (items.length < 1) {
                return 0;
            }

            const dateCreated = this.$moment(items[items.length - 1].CreatedDate);
            const today = this.$moment();

            return this.$moment.duration(today.diff(dateCreated)).asDays();
        },

        getMessages() {
            SocialMediaPostConversationsService.getPrivateMessage(
                this.options.messageId,
                this.options.clientId
            )
                .then(({ data }) => {
                    const message = data.data;

                    this.messages = message.PrivateMessages;
                    this.recipientId = message.RecipientId;
                    this.senderId = message.SenderId;
                    const { RecipientImage, SenderImage } = message;
                    this.usersProfileImages = [
                        {
                            type: 'recipient',
                            url: RecipientImage ? RecipientImage.Url : null,
                            mimeType: RecipientImage ? RecipientImage.MimeType : null,
                        },
                        {
                            type: 'sender',
                            url: SenderImage ? SenderImage.Url : null,
                            mimeType: SenderImage ? SenderImage.MimeType : null,
                        },
                    ];

                    const daysAgo = this.getLastSenderMessageDay();
                    this.hasReachedTimeLimit = daysAgo >= 7;
                    EventBus.$emit('social-inbox:getConversations');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting post details. Please try again.',
                        { error },
                    );
                    this.closeWidget();
                });
        },

        handleClick() {
            if (this.options.isClosed) {
                this.reopenConversation();
            } else {
                const hasMedia = this.replyMessage.image && this.replyMessage.image.length > 0;

                let params = {
                    messageId: this.options.messageId,
                    message: this.replyMessage.message,
                };

                if (hasMedia) {
                    const {
                        fileId,
                        isLibrary,
                        mediaUrl,
                        fileUrl = null,
                    } = this.replyMessage.image[0];

                    if (isLibrary) {
                        params = { ...params, url: fileUrl, previewImageUrl: mediaUrl };
                    }

                    params = { ...params, fileId };
                }

                if (this.replyMessage.message || hasMedia) {
                    this.sendMessage(params).then(() => {
                        const element = document.getElementById('socialInboxMessageHistory');

                        this.$refs.replyMessageInput.clearInput();

                        if (this.options.socialNetwork === 'twitter' && hasMedia) {
                            setTimeout(async () => await this.getMessages(), 4000);
                        } else {
                            this.getMessages();
                        }

                        if (element) {
                            window.scrollTo(0, element.scrollHeight);
                        }

                        this.$helpers.showSuccessNotification('The message was sent succesfully');
                    });
                }
            }
        },

        initMessagesHistoryInterval() {
            this.messagesRefreshInterval = setInterval(() => this.getMessages(), 5000);
        },

        initMouseMoveDetection() {
            if (this.mouseDetectionTimeOut) {
                clearTimeout(this.mouseDetectionTimeOut);
                this.mouseDetectionTimeOut = null;

                if (!this.messagesRefreshInterval) {
                    this.initMessagesHistoryInterval();
                }
            }

            this.mouseDetectionTimeOut = setTimeout(
                () => this.destroyMessagesRefreshInterval(),
                180000
            );
        },

        reopenConversation() {
            this.disableBtn = true;
            this.loadingBtn = true;

            SocialMediaPostConversationsService.togglePrivateMessageStatus(this.options.clientId, {
                messagesIds: [this.options.messageId],
                isClosed: false,
            })
                .then(({ data }) => {
                    if (data.message === 'Ok') {
                        this.disableBtn = false;
                        this.$helpers.showSuccessNotification(
                            'The message was re-opened succesfully'
                        );

                        EventBus.$emit('social-inbox:getConversations');
                        this.closeWidget();
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while trying to re-open the conversation. Please try again.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loadingBtn = false;
                    this.disableBtn = false;
                });
        },

        sendMessage(params) {
            this.disableBtn = true;
            this.loadingBtn = true;

            return new Promise((resolve) => {
                SocialMediaPostConversationsService.postPrivateMessage(
                    this.options.clientId,
                    this.options.socialNetwork,
                    params
                )
                    .then(({ data }) => {
                        if (data.message === 'Ok') {
                            resolve();
                        }
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while trying to reply the message. Please try again.',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.loadingBtn = false;
                        this.disableBtn = false;
                    });
            });
        },
    },
};
</script>

<style>
.social-media-inbox-private-messages-widget .loading-screen-container {
    height: 200px;
    overflow: hidden;
    text-align: center;
}

.social-media-inbox-private-messages-widget .loading-screen-container .loading-screen {
    height: 150px;
}

.social-media-inbox-private-messages-widget .widget-footer .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
    border: 1px solid #877d88;
}

.social-media-inbox-private-messages-widget
    .widget-footer
    .cancel-container
    .btn.transparent-btn:hover {
    background-color: #e1e0e5;
}

.social-media-inbox-private-messages-widget .widget-header .private-message-icon {
    margin-right: 16px;
}

.social-media-inbox-private-messages-widget .widget-header .p22-private-messages-svg .a {
    fill: #fff;
}

.social-media-inbox-private-messages-widget .content-wrapper {
    height: calc(100vh - 170px);
}

.social-media-inbox-private-messages-widget .social-inbox-reply-message {
    top: 32px;
}

.social-media-inbox-private-messages-widget .social-inbox-reply-message .input-container {
    border: 2px solid #e6e4e6;
    border-radius: 27px;
}

.social-media-inbox-private-messages-widget .content-wrapper .card-conversation-closed {
    font: normal normal normal 14px/17px Roboto;
    color: #382153;
    border-radius: 10px;
    border: 1px solid #e66001;
    padding: 25px 5px;
    margin-bottom: 20px;
}

.social-media-inbox-private-messages-widget .text-capitalize {
    text-transform: capitalize !important;
}

.social-media-inbox-private-messages-widget .widget-footer .action-container .btn.white-btn {
    background-color: #fff;
    color: #18aeff;
    border-color: #18aeff;
}

.social-media-inbox-private-messages-widget .widget-footer .action-container .btn.white-btn:hover {
    background-color: #18aeff;
    color: #fff;
}

.social-media-inbox-private-messages-widget .widget-footer .action-container .btn.white-btn svg {
    width: 27px;
    height: 27px;
}

.social-media-inbox-private-messages-widget .widget-footer .action-container .btn.white-btn span {
    padding-left: 10px;
}
</style>
